import { useEffect, useState } from 'react';
import { getSharedView, unlockSharedView } from '@/features/sharing/api/sharing-api';
import { normalizeEditorGraph } from '@/features/funnel-editor/domain/normalize-graph';
import { PresentationView } from '@/features/presentation/PresentationPage';
import type { EditorGraph } from '@/features/funnel-editor/domain/editor-graph';

type State =
  | { status: 'loading' }
  | { status: 'password'; title?: string; error?: string }
  | { status: 'ready'; name: string; graph: EditorGraph }
  | { status: 'unavailable' };

export function SharedPresentationPage({ token }: { token: string }) {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [password, setPassword] = useState('');
  async function load() {
    try {
      const result = await getSharedView(token);
      if (result.status === 'password_required')
        setState({ status: 'password', title: result.title });
      else
        setState({
          status: 'ready',
          name: result.funnel.name,
          graph: normalizeEditorGraph(result.funnel.graph),
        });
    } catch {
      setState({ status: 'unavailable' });
    }
  }
  useEffect(() => {
    document.title = 'Funil compartilhado · Dark Funnels';
    let meta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      document.head.appendChild(meta as never);
    }
    meta.content = 'noindex, nofollow';
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  if (state.status === 'loading')
    return (
      <main role="status" className="flex h-screen items-center justify-center">
        Carregando funil compartilhado…
      </main>
    );
  if (state.status === 'unavailable')
    return (
      <main role="alert" className="flex h-screen items-center justify-center">
        Este funil compartilhado está indisponível.
      </main>
    );
  if (state.status === 'password') {
    return (
      <main className="flex h-screen items-center justify-center bg-bg-app">
        <form
          aria-label="Senha do funil compartilhado"
          className="flex w-80 flex-col gap-3 rounded border border-border-subtle bg-surface p-6"
          onSubmit={async (event) => {
            event.preventDefault();
            try {
              await unlockSharedView(token, password);
              await load();
            } catch (error) {
              setState({
                ...state,
                error: error instanceof Error ? error.message : 'Senha incorreta.',
              });
            }
          }}
        >
          <h1 className="font-semibold">{state.title ?? 'Funil protegido'}</h1>
          <label>
            Senha
            <input
              aria-label="Senha"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded bg-bg-app p-2"
            />
          </label>
          {state.error ? <p role="alert">{state.error}</p> : null}
          <button className="rounded border border-border-brand p-2">Ver funil</button>
        </form>
      </main>
    );
  }
  return (
    <PresentationView
      graph={state.graph}
      funnelName={state.name}
      funnelId="shared"
      publicView
      publicToken={token}
    />
  );
}
